// src/pages/workflow/WorkVendorPortal.js
import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { db, storage } from "../../firebaseConfig";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import toast, { Toaster } from "react-hot-toast";
import {
  isNafedProject,
  queueWorkflowMail,
  upper,
} from "./nafedWorkflowBridge";

/* ================== CONFIG ================== */
const ROLE_OPTIONS = [
  { id: "SURVEY", label: "Survey Engineer / Agency" },
  { id: "SOIL_TEST", label: "Soil Testing Lab" },
  { id: "DESIGN", label: "Structural / Design Engineer" },
  { id: "ESTIMATE", label: "Estimator / QS" },
  { id: "DPR_CA", label: "CA / DPR Consultant" },
];

/* ================== SMALL UI ATOMS ================== */
function Label({ children }) {
  return (
    <label className="text-sm font-semibold text-gray-700">{children}</label>
  );
}
function Input({ type = "text", value, onChange, ...p }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
      {...p}
    />
  );
}
function Checkbox({ checked, onChange }) {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="w-4 h-4 text-emerald-600"
    />
  );
}
function Card({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-emerald-100 bg-white shadow-md">
      {title && (
        <div className="px-5 py-3 border-b border-emerald-50 bg-gradient-to-r from-emerald-600 to-teal-500 text-white rounded-t-2xl">
          <div className="text-sm font-semibold">{title}</div>
          {subtitle && (
            <div className="text-[11px] text-emerald-100 mt-0.5">
              {subtitle}
            </div>
          )}
        </div>
      )}
      <div className="p-5">
        <div className="grid gap-4">{children}</div>
      </div>
    </div>
  );
}
function Badge({ color = "gray", children }) {
  const map = {
    green: "bg-emerald-100 text-emerald-700",
    yellow: "bg-amber-100 text-amber-700",
    blue: "bg-sky-100 text-sky-700",
    gray: "bg-gray-100 text-gray-700",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
        map[color] || map.gray
      }`}
    >
      {children}
    </span>
  );
}

/* ================== PAGE ================== */
export default function WorkVendorPortal() {
  const auth = useMemo(() => getAuth(), []);
  const [tab, setTab] = useState("login"); // 'login' | 'register'
  const [dark, setDark] = useState(false);

  /* ---------- REGISTER STATE ---------- */
  const [rName, setRName] = useState("");
  const [rFirm, setRFirm] = useState("");
  const [rEmail, setREmail] = useState("");
  const [rPhone, setRPhone] = useState("");
  const [rPassword, setRPassword] = useState("");
  const [rRoles, setRRoles] = useState([]);
  const [rLoading, setRLoading] = useState(false);

  /* ---------- LOGIN STATE ---------- */
  const [lEmail, setLEmail] = useState("");
  const [lPassword, setLPassword] = useState("");
  const [lLoading, setLLoading] = useState(false);

  /* ---------- AFTER LOGIN ---------- */
  const [me, setMe] = useState(null); // work_vendors doc
  const [selectedRole, setSelectedRole] = useState("");
  const [tasks, setTasks] = useState([]); // tasks + project info + linked files
  const [activeTask, setActiveTask] = useState(null); // modal me open wala task
  const [tLoading, setTLoading] = useState(false);
  const [uploadingTaskId, setUploadingTaskId] = useState(null);

  const loggedIn = !!me;
  const uiRoleLabel = (id) =>
    ROLE_OPTIONS.find((r) => r.id === id)?.label || id;

  const doLogout = async () => {
    await signOut(auth);
    setMe(null);
    setSelectedRole("");
    setTasks([]);
    setActiveTask(null);
    setTab("login");
  };

  /* ---------- REGISTER ---------- */
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!rName || !rEmail || !rPassword || !rFirm) {
      toast.error("Please fill Name, Firm, Email, Password");
      return;
    }
    if (!rRoles.length) {
      toast.error("Please select at least one work category");
      return;
    }

    try {
      setRLoading(true);
      const email = rEmail.trim().toLowerCase();
      const cred = await createUserWithEmailAndPassword(auth, email, rPassword);

      const uid = cred.user.uid;

      await setDoc(doc(db, "work_vendors", uid), {
        uid,
        name: rName.trim(),
        firmName: rFirm.trim(),
        email,
        phone: (rPhone || "").replace(/\D/g, "").slice(0, 10),
        categories: rRoles,
        approvedCategories: [],
        status: "pending",
        controlledByPortal: "SHINE_AGRI",
        approvalAuthorityRole: "SHINE_AGRI_ADMIN",
        approvalAuthorityLabel: "Shine Agri Portal Administration",
        nafedVendor: true,
        visibleToApprovedNafedPmcs: false,
        isActive: false,
        isDeleted: false,
        createdAt: serverTimestamp(),
        approvedAt: null,
        updatedAt: serverTimestamp(),
      });

      toast.success(
        "Registration submitted. Admin approval ke baad aapko mail ayega.",
      );
      setTab("login");
    } catch (err) {
      console.error(err);
      if (err?.code === "auth/email-already-in-use") {
        toast.error("Ye email already registered hai. Please login.");
      } else {
        toast.error(err?.message || "Registration failed.");
      }
    } finally {
      setRLoading(false);
    }
  };

  /* ---------- LOGIN ---------- */
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!lEmail || !lPassword) {
      toast.error("Enter email & password");
      return;
    }
    try {
      setLLoading(true);
      const email = lEmail.trim().toLowerCase();
      const { user } = await signInWithEmailAndPassword(auth, email, lPassword);

      const vSnap = await getDoc(doc(db, "work_vendors", user.uid));
      if (!vSnap.exists()) {
        await signOut(auth);
        toast.error("Work vendor registration not found.");
        return;
      }
      const v = vSnap.data();

      if (
        v.status !== "approved" ||
        v.isActive === false ||
        v.isDeleted === true
      ) {
        await signOut(auth);
        toast.error(
          "Your profile is pending approval. Please wait for admin confirmation.",
        );
        return;
      }

      if (!v.approvedCategories || !v.approvedCategories.length) {
        await signOut(auth);
        toast.error("No work categories approved yet. Please contact admin.");
        return;
      }

      setMe(v);
      setSelectedRole("");
      setTasks([]);
      setActiveTask(null);
      toast.success("Login successful. Please choose work category.");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Login failed.");
    } finally {
      setLLoading(false);
    }
  };

  /* ---------- LOAD TASKS (role-wise + linked files) ---------- */
  useEffect(() => {
    const loadTasks = async () => {
      if (!me || !selectedRole) {
        setTasks([]);
        setActiveTask(null);
        return;
      }

      try {
        setTLoading(true);

        const qRef = query(
          collection(db, "wf_project_tasks"),
          where("vendorId", "==", me.uid),
          where("type", "==", selectedRole),
        );
        const tSnap = await getDocs(qRef);
        const rawTasks = [];
        tSnap.forEach((d) => rawTasks.push({ id: d.id, ...d.data() }));

        const projectsMap = {};
        const projectTasksMap = {};
        const projectIds = Array.from(
          new Set(rawTasks.map((t) => t.projectId).filter(Boolean)),
        );

        for (const pid of projectIds) {
          const pSnap = await getDoc(doc(db, "wf_projects", pid));
          const project = pSnap.exists()
            ? { id: pSnap.id, ...pSnap.data() }
            : null;
          projectsMap[pid] =
            project && isNafedProject(project) ? project : null;

          const ptSnap = await getDocs(
            query(
              collection(db, "wf_project_tasks"),
              where("projectId", "==", pid),
            ),
          );
          const list = [];
          ptSnap.forEach((d) => list.push({ id: d.id, ...d.data() }));
          projectTasksMap[pid] = list;
        }

        function getLinkedFiles(task) {
          const projectTasks = projectTasksMap[task.projectId] || [];
          const allFiles = (typeArr) =>
            projectTasks
              .filter((t) => typeArr.includes(t.type))
              .flatMap((t) => t.files || []);

          if (task.type === "DESIGN") {
            return allFiles(["SURVEY", "SOIL_TEST"]);
          }
          if (task.type === "ESTIMATE") {
            return allFiles(["DESIGN"]);
          }
          if (task.type === "DPR_CA") {
            return allFiles(["ESTIMATE"]);
          }
          return [];
        }

        const combined = rawTasks
          .filter((t) => t.nafedProject === true && projectsMap[t.projectId])
          .map((t) => ({
            ...t,
            project: projectsMap[t.projectId],
            linkedFiles: getLinkedFiles(t),
          }));

        setTasks(combined);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load tasks");
      } finally {
        setTLoading(false);
      }
    };

    loadTasks();
  }, [me, selectedRole]);

  /* ---------- TASK ACTIONS (Accept / Complete) ---------- */

  const acceptTask = async (task) => {
    const when = window.prompt(
      "Survey / visit date & time likhiye (e.g. 2025-12-01 11:00 AM):",
      task.plannedDateTime || "",
    );
    if (!when) return;

    try {
      await updateDoc(doc(db, "wf_project_tasks", task.id), {
        status: "ACCEPTED",
        plannedDateTime: when,
        acceptedAt: serverTimestamp(),
      });
      toast.success("Task accepted & planned.");
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? { ...t, status: "ACCEPTED", plannedDateTime: when }
            : t,
        ),
      );
      setActiveTask((prev) =>
        prev && prev.id === task.id
          ? { ...prev, status: "ACCEPTED", plannedDateTime: when }
          : prev,
      );
    } catch (e) {
      console.error(e);
      toast.error("Failed to accept task");
    }
  };

  const completeTask = async (task) => {
    const ok = window.confirm(
      "Kya aap sure ho ki ye kaam (Survey / Soil / Design / etc.) complete ho chuka hai?",
    );
    if (!ok) return;

    try {
      if (!(task.files || []).length) {
        toast.error(
          "PMC review ke liye kam se kam ek report/document upload karo.",
        );
        return;
      }
      let finalAmount = Number(task.finalAmount || 0);
      if (upper(task.type) === "DPR_CA") {
        const entered = window.prompt(
          "Final DPR amount (without changing after approval):",
          finalAmount || "",
        );
        finalAmount = Number(entered || 0);
        if (finalAmount <= 0) {
          toast.error("Final DPR amount mandatory hai.");
          return;
        }
      }
      await updateDoc(doc(db, "wf_project_tasks", task.id), {
        status: "SUBMITTED",
        pmcStatus: "UNDER_PMC_REVIEW",
        adminStatus: "PENDING",
        finalAmount:
          upper(task.type) === "DPR_CA"
            ? finalAmount
            : Number(task.finalAmount || 0),
        completedAt: serverTimestamp(),
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await queueWorkflowMail(db, {
        to: task.project?.allocatedPmcEmail || task.project?.pmcEmail || "",
        subject: `NAFED ${task.type} submitted — ${task.project?.pacsName || "PACS"}`,
        html: `<p>Vendor <b>${me?.firmName || me?.name}</b> ne <b>${task.type}</b> work PMC review ke liye submit kiya hai.</p>`,
        event: "NAFED_VENDOR_WORK_SUBMITTED",
        projectId: task.projectId,
      });
      toast.success("Work PMC review ke liye submit ho gaya.");
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? {
                ...t,
                status: "SUBMITTED",
                pmcStatus: "UNDER_PMC_REVIEW",
                finalAmount,
              }
            : t,
        ),
      );
      setActiveTask((prev) =>
        prev && prev.id === task.id
          ? {
              ...prev,
              status: "SUBMITTED",
              pmcStatus: "UNDER_PMC_REVIEW",
              finalAmount,
            }
          : prev,
      );
    } catch (e) {
      console.error(e);
      toast.error("Failed to mark as completed");
    }
  };

  /* ---------- FILE UPLOAD (Report / Photos / Video / ZIP etc.) ---------- */

  const uploadFilesForTask = async (task, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    try {
      setUploadingTaskId(task.id);
      const uploaded = [];

      for (const file of files) {
        const path = `workflow/project_files/${task.projectId}/${task.type}/${task.id}/${Date.now()}_${file.name}`;
        const ref = storageRef(storage, path);
        await uploadBytes(ref, file);
        const url = await getDownloadURL(ref);

        uploaded.push({
          url,
          name: file.name,
          uploadedAt: new Date().toISOString(),
        });
      }

      await updateDoc(doc(db, "wf_project_tasks", task.id), {
        files: arrayUnion(...uploaded),
      });

      toast.success("Files uploaded successfully.");
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? { ...t, files: [...(t.files || []), ...uploaded] }
            : t,
        ),
      );
      setActiveTask((prev) =>
        prev && prev.id === task.id
          ? { ...prev, files: [...(prev.files || []), ...uploaded] }
          : prev,
      );
    } catch (e) {
      console.error("Upload failed:", e);
      toast.error(
        e?.message ||
          "File upload failed. Please check Storage rules / network.",
      );
    } finally {
      setUploadingTaskId(null);
    }
  };

  /* ---------- REMARKS (per task / per stage) ---------- */

  const handleRemarkChange = (taskId, value) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, vendorRemark: value } : t)),
    );
    setActiveTask((prev) =>
      prev && prev.id === taskId ? { ...prev, vendorRemark: value } : prev,
    );
  };

  const saveRemark = async (task) => {
    try {
      await updateDoc(doc(db, "wf_project_tasks", task.id), {
        vendorRemark: task.vendorRemark || "",
        remarkUpdatedAt: serverTimestamp(),
      });
      toast.success("Remark saved");
    } catch (e) {
      console.error(e);
      toast.error("Failed to save remark");
    }
  };

  /* ================== RENDER ================== */
  const header = (
    <div className="rounded-2xl bg-white shadow-lg border border-emerald-200 px-6 py-5 mb-6 flex items-center justify-between">
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold text-emerald-800 tracking-tight">
          Project Work Vendor Portal
        </h1>
        <p className="text-sm text-gray-600">
          Survey • Soil Testing • Design • Estimate • DPR – aapko assign hua
          kaam yahan dikhega (enterprise-grade workflow).
        </p>
      </div>
      <div className="flex items-center gap-2">
        {loggedIn && (
          <button
            onClick={doLogout}
            className="text-xs px-3 py-2 rounded-lg bg-gray-900 text-white shadow hover:bg-black"
          >
            Logout ({me?.name})
          </button>
        )}
        <button
          onClick={() => setDark((d) => !d)}
          className="text-xs px-3 py-2 rounded-lg border hover:bg-emerald-50"
        >
          {dark ? "Light" : "Dark"}
        </button>
      </div>
    </div>
  );

  const renderProgressBar = (status) => {
    let pct = 10;
    if (status === "ACCEPTED") pct = 40;
    else if (status === "IN_PROGRESS") pct = 70;
    else if (status === "COMPLETED") pct = 100;

    return (
      <div className="w-full">
        <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-2 bg-emerald-500 rounded-full"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-1 text-[11px] text-gray-600">
          {pct}% • {status || "ASSIGNED"}
        </div>
      </div>
    );
  };

  const getLastUpdated = (t) => {
    const ts = t.completedAt || t.acceptedAt || t.assignedAt;
    if (ts?.toDate) return ts.toDate().toLocaleString();
    return "—";
  };

  return (
    <>
      <Toaster position="top-center" />
      <div
        className={`min-h-screen ${
          dark ? "bg-slate-900 text-white" : "bg-emerald-50"
        }`}
      >
        <div className="max-w-5xl mx-auto px-4 py-8">
          {header}

          {/* LOGGED IN VIEW / LOGIN VIEW */}
          {loggedIn ? (
            <div className="grid gap-4">
              <Card
                title="Select Work Category"
                subtitle="Apko jo role ke liye approve kiya gaya hai, wahi category yahan dikhegi."
              >
                <div className="flex flex-wrap gap-2">
                  {me.approvedCategories.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => {
                        setSelectedRole(r);
                        setActiveTask(null);
                      }}
                      className={`px-4 py-2 rounded-full text-sm border shadow-sm ${
                        selectedRole === r
                          ? "bg-emerald-600 text-white border-emerald-700"
                          : "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      }`}
                    >
                      {uiRoleLabel(r)}
                    </button>
                  ))}
                </div>
              </Card>

              <Card
                title={
                  selectedRole
                    ? `${uiRoleLabel(selectedRole)} – My Tasks`
                    : "My Tasks"
                }
                subtitle={
                  selectedRole
                    ? "Neeche sirf project register hai. Detail dekhne ke liye View » dabaiye."
                    : undefined
                }
              >
                {!selectedRole && (
                  <div className="text-sm text-gray-500">
                    Pehle upar se koi work category select kijiye.
                  </div>
                )}

                {selectedRole && tLoading && (
                  <div className="text-sm text-gray-500">Loading tasks…</div>
                )}

                {selectedRole && !tLoading && !tasks.length && (
                  <div className="text-sm text-gray-500">
                    Abhi is category ke liye koi active task assign nahi hai.
                    Jaise hi Admin MOU ke baad aapko select karega, yahan dikh
                    jayega.
                  </div>
                )}

                {selectedRole && !tLoading && tasks.length > 0 && (
                  <div className="rounded-2xl bg-white border border-gray-900 overflow-x-auto">
                    <table className="min-w-full text-sm bg-white">
                      <thead className="bg-gray-50 border-b border-gray-900">
                        <tr className="text-gray-800">
                          <th className="px-3 py-2 text-left border-r border-gray-900 w-10">
                            #
                          </th>
                          <th className="px-3 py-2 text-left border-r border-gray-900">
                            PACS / Project
                          </th>
                          <th className="px-3 py-2 text-left border-r border-gray-900 w-40">
                            District
                          </th>
                          <th className="px-3 py-2 text-left border-r border-gray-900 w-40">
                            Stage
                          </th>
                          <th className="px-3 py-2 text-left border-r border-gray-900 w-48">
                            Progress
                          </th>
                          <th className="px-3 py-2 text-left border-r border-gray-900 w-56">
                            Last Updated
                          </th>
                          <th className="px-3 py-2 text-left w-32">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.map((t, idx) => {
                          const p = t.project || {};
                          const statusColor =
                            t.status === "COMPLETED"
                              ? "green"
                              : t.status === "ACCEPTED" ||
                                  t.status === "IN_PROGRESS"
                                ? "blue"
                                : "yellow";

                          return (
                            <tr
                              key={t.id}
                              className="border-t border-gray-900 hover:bg-emerald-50"
                            >
                              <td className="px-3 py-2 border-r border-gray-900">
                                {idx + 1}
                              </td>
                              <td className="px-3 py-2 border-r border-gray-900">
                                <div className="font-semibold text-gray-900">
                                  {p.title || "Untitled Project"}
                                </div>
                                <div className="text-xs text-gray-600">
                                  {p.pacsName || "— PACS —"}
                                </div>
                                <div className="text-[11px] text-gray-400">
                                  Task ID: {t.id.slice(0, 8)}…
                                </div>
                              </td>
                              <td className="px-3 py-2 border-r border-gray-900 text-sm">
                                {p.district || "—"}
                              </td>
                              <td className="px-3 py-2 border-r border-gray-900 text-sm">
                                <div className="mb-1 text-xs text-gray-600">
                                  {uiRoleLabel(t.type)}
                                </div>
                                <Badge color={statusColor}>
                                  {t.status || "ASSIGNED"}
                                </Badge>
                              </td>
                              <td className="px-3 py-2 border-r border-gray-900">
                                {renderProgressBar(t.status)}
                              </td>
                              <td className="px-3 py-2 border-r border-gray-900 text-sm">
                                {getLastUpdated(t)}
                              </td>
                              <td className="px-3 py-2 text-sm">
                                <button
                                  type="button"
                                  onClick={() => setActiveTask(t)}
                                  className="inline-flex items-center px-4 py-1.5 rounded-full border border-emerald-700 text-emerald-800 text-sm font-semibold hover:bg-emerald-50"
                                >
                                  View &raquo;
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              {/* ============ DETAIL MODAL ============ */}
              {activeTask && (
                <div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/40 overflow-y-auto">
                  <div className="bg-white text-gray-900 rounded-2xl shadow-2xl max-w-4xl w-full mx-4 mt-20 mb-10 max-h-[calc(100vh-5rem)] overflow-y-auto p-6">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                          Task Detail Window
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">
                          {activeTask.project?.title || "Untitled Project"}
                        </h2>
                        <div className="text-sm text-gray-600">
                          {activeTask.project?.pacsName || "— PACS —"}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveTask(null)}
                        className="px-3 py-1.5 rounded-full border text-sm hover:bg-gray-100"
                      >
                        Close ✕
                      </button>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="border rounded-xl p-3">
                        <div className="text-xs font-semibold text-gray-500 mb-1">
                          Location
                        </div>
                        <div className="text-sm">
                          {activeTask.project?.locationVillage || "—"},{" "}
                          {activeTask.project?.taluka || "—"},{" "}
                          {activeTask.project?.district || "—"},{" "}
                          {activeTask.project?.state || "—"}
                        </div>
                        {activeTask.project?.googleMapsUrl && (
                          <a
                            href={activeTask.project.googleMapsUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 inline-block text-xs text-emerald-700 underline"
                          >
                            Open in Google Maps
                          </a>
                        )}
                      </div>

                      <div className="border rounded-xl p-3">
                        <div className="text-xs font-semibold text-gray-500 mb-1">
                          Stage &amp; Status
                        </div>
                        <div className="text-sm mb-1">
                          {uiRoleLabel(activeTask.type)}
                        </div>
                        <Badge
                          color={
                            activeTask.status === "COMPLETED"
                              ? "green"
                              : activeTask.status === "ACCEPTED" ||
                                  activeTask.status === "IN_PROGRESS"
                                ? "blue"
                                : "yellow"
                          }
                        >
                          {activeTask.status || "ASSIGNED"}
                        </Badge>
                      </div>
                    </div>

                    <div className="border rounded-xl p-3 mb-4 text-sm space-y-1">
                      <div className="text-xs font-semibold text-gray-500">
                        Timeline
                      </div>
                      <div>
                        <b>Assigned:</b>{" "}
                        {activeTask.assignedAt?.toDate
                          ? activeTask.assignedAt.toDate().toLocaleString()
                          : "—"}
                      </div>
                      <div>
                        <b>Planned:</b> {activeTask.plannedDateTime || "—"}
                      </div>
                      <div>
                        <b>Completed:</b>{" "}
                        {activeTask.completedAt?.toDate
                          ? activeTask.completedAt.toDate().toLocaleString()
                          : "—"}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="border rounded-xl p-3 text-sm">
                        <div className="text-xs font-semibold text-gray-500 mb-1">
                          Previous Stage Documents
                        </div>
                        {activeTask.linkedFiles &&
                        activeTask.linkedFiles.length ? (
                          <ul className="list-disc ml-4 space-y-1">
                            {activeTask.linkedFiles.map((f, i) => (
                              <li key={i}>
                                <a
                                  href={f.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-emerald-700 underline"
                                >
                                  {f.name || `File ${i + 1}`}
                                </a>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="text-gray-400">No previous docs</div>
                        )}
                      </div>

                      <div className="border rounded-xl p-3 text-sm">
                        <div className="text-xs font-semibold text-gray-500 mb-1">
                          Your Uploaded Files
                        </div>
                        {activeTask.files && activeTask.files.length ? (
                          <ul className="list-disc ml-4 space-y-1">
                            {activeTask.files.map((f, i) => (
                              <li key={i}>
                                <a
                                  href={f.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-emerald-700 underline"
                                >
                                  {f.name || `File ${i + 1}`}
                                </a>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="text-gray-400">
                            No files uploaded yet
                          </div>
                        )}

                        <div className="mt-3 text-xs">
                          <span className="block mb-1">
                            Upload report / ZIP:
                          </span>
                          <input
                            type="file"
                            multiple
                            accept=".pdf,.zip,.rar,.jpg,.jpeg,.png"
                            disabled={uploadingTaskId === activeTask.id}
                            onChange={(e) => {
                              const fl = e.target.files;
                              if (fl && fl.length) {
                                uploadFilesForTask(activeTask, fl);
                                e.target.value = "";
                              }
                            }}
                            className="text-xs"
                          />
                          {uploadingTaskId === activeTask.id && (
                            <div className="mt-1 text-emerald-700">
                              Uploading…
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="border rounded-xl p-3 mb-4">
                      <div className="text-xs font-semibold text-gray-500 mb-1">
                        Remark / Note for Next Stage
                      </div>
                      <textarea
                        rows={3}
                        value={activeTask.vendorRemark || ""}
                        onChange={(e) =>
                          handleRemarkChange(activeTask.id, e.target.value)
                        }
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                        placeholder="Notes for next stage…"
                      />
                      <button
                        type="button"
                        onClick={() => saveRemark(activeTask)}
                        className="mt-2 px-4 py-1.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
                      >
                        Save Remark
                      </button>
                    </div>

                    <div className="flex flex-wrap justify-end gap-2">
                      {activeTask.status === "ASSIGNED" && (
                        <button
                          type="button"
                          onClick={() => acceptTask(activeTask)}
                          className="px-4 py-1.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
                        >
                          Accept &amp; Plan
                        </button>
                      )}

                      {["ACCEPTED", "IN_PROGRESS"].includes(
                        activeTask.status,
                      ) && (
                        <button
                          type="button"
                          onClick={() => completeTask(activeTask)}
                          className="px-4 py-1.5 rounded-full border border-emerald-600 text-emerald-700 text-sm font-semibold hover:bg-emerald-50"
                        >
                          Mark Completed
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="flex justify-center gap-2 mb-6">
                {["login", "register"].map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`px-5 py-2 rounded-xl text-sm font-semibold border ${
                      tab === t
                        ? "bg-emerald-600 text-white border-emerald-700"
                        : "bg-white text-emerald-700 border-emerald-200"
                    }`}
                  >
                    {t === "login" ? "Login" : "New Registration"}
                  </button>
                ))}
              </div>

              {/* Card – LOGIN / REGISTER */}
              <div className="rounded-2xl bg-white shadow-xl border border-gray-200 p-6">
                {tab === "login" && (
                  <form
                    onSubmit={handleLogin}
                    className="max-w-md mx-auto grid gap-4"
                  >
                    <Label>Email</Label>
                    <Input type="email" value={lEmail} onChange={setLEmail} />
                    <Label>Password</Label>
                    <Input
                      type="password"
                      value={lPassword}
                      onChange={setLPassword}
                    />
                    <button
                      disabled={lLoading}
                      className="mt-2 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                    >
                      {lLoading ? "Logging in…" : "Login"}
                    </button>
                  </form>
                )}

                {tab === "register" && (
                  <form
                    onSubmit={handleRegister}
                    className="max-w-2xl mx-auto grid gap-4"
                  >
                    <Card title="Basic Details">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Full Name</Label>
                          <Input value={rName} onChange={setRName} />
                        </div>
                        <div>
                          <Label>Firm / Company Name</Label>
                          <Input value={rFirm} onChange={setRFirm} />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Email (Login ID)</Label>
                          <Input
                            type="email"
                            value={rEmail}
                            onChange={setREmail}
                          />
                        </div>
                        <div>
                          <Label>Mobile</Label>
                          <Input
                            value={rPhone}
                            onChange={(v) =>
                              setRPhone(v.replace(/\D/g, "").slice(0, 10))
                            }
                          />
                        </div>
                      </div>
                    </Card>

                    <Card title="Work Categories (select all applicable)">
                      <div className="grid md:grid-cols-2 gap-3">
                        {ROLE_OPTIONS.map((r) => (
                          <label
                            key={r.id}
                            className="flex items-center gap-3 text-sm"
                          >
                            <Checkbox
                              checked={rRoles.includes(r.id)}
                              onChange={(checked) => {
                                setRRoles((old) =>
                                  checked
                                    ? [...old, r.id]
                                    : old.filter((x) => x !== r.id),
                                );
                              }}
                            />
                            <span>{r.label}</span>
                          </label>
                        ))}
                      </div>
                    </Card>

                    <Card title="Login Password">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Set Password</Label>
                          <Input
                            type="password"
                            value={rPassword}
                            onChange={setRPassword}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Minimum 6 characters recommended.
                          </p>
                        </div>
                      </div>
                    </Card>

                    <div className="flex justify-end mt-4">
                      <button
                        disabled={rLoading}
                        className="px-6 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold"
                      >
                        {rLoading ? "Submitting…" : "Submit Registration"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </>
          )}

          <div className="text-center mt-8 text-sm">
            <Link to="/" className="text-emerald-700 underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
